/**
 * The verified patch engine every autofix in this layer has to go through.
 *
 * An autofix in a modding tool is a promise made about a file that the tool read
 * some time ago. Between the read and the write the user renames a modkit, saves
 * from OpenIV, pulls a newer version of the pack, or simply runs the report
 * twice and clicks the button from the first run. If a fix is nothing but an
 * offset and a replacement, every one of those situations ends the same way: the
 * replacement lands somewhere it was never calculated for, and the damage is
 * silent. A `carcols.meta` with a modkit id pasted into the middle of a `<Item>`
 * tag does not fail to load loudly — the vehicle simply spawns with no mods and
 * the user has no reason to connect that to a button they pressed an hour ago.
 *
 * So the unit of change here is not an offset. It is an offset plus the text
 * that is supposed to be sitting at it. An offset alone is an unverifiable
 * promise; an offset plus the expected text is a promise that can be checked and
 * refused, and refusing is always cheaper than repairing a stranger's resource.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE NAIVE VERSION IS WRONG — four failures this exists to prevent
 *
 * 1. Applying front-to-back. The first replacement changes the length of the
 *    text, so every later offset is off by the delta and the second edit lands
 *    inside the wrong tag. Edits are applied back-to-front here so that earlier
 *    offsets stay meaningful for the whole pass.
 *
 * 2. Using `RegExp.test` for the expectation. `test` answers "is this pattern
 *    somewhere in the span", which is not the question. `/\d+/` is "found" in
 *    `<id value="14"/>` and in `<id value="1400"/>` alike, so a fix calculated
 *    for one verifies happily against the other. A pattern here has to match the
 *    whole span or it is not the text the fix was calculated from.
 *
 * 3. Picking a winner when two fixes touch the same characters. Two rules can
 *    both want to rewrite the same modkit id for different reasons, and applying
 *    either one means the other's finding is now stale and its offsets are
 *    wrong. Overlapping edits are rejected in pairs rather than ordered, because
 *    half of one fix plus half of another is a state neither rule intended.
 *
 * 4. Dropping a rejected edit quietly. A fix-all button that says "12 fixed"
 *    when it applied nine is worse than one that refuses outright, because the
 *    user stops checking. Every rejection comes back with a sentence saying what
 *    was refused and why, and the caller decides what to do about it.
 *
 * `applyEdits` never throws and never half-applies: the text it returns is the
 * source with exactly the edits in `applied` performed on it, or the untouched
 * source when nothing survived verification.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * LIMITS. This engine knows nothing about the grammar of the file it is
 * patching. It cannot tell that a replacement produces malformed XML or a Lua
 * table that no longer closes; that is the job of whoever built the edit. It
 * also works in UTF-16 code units, which is what `String.prototype.slice` and
 * every offset produced by this layer's parsers use — a "byte" offset in
 * `GtaSourceSpan` is really a JavaScript string index, and the two only diverge
 * for files with non-ASCII content, which the callers never index into anyway.
 */

import { pathKey } from "./normalize";
import type { GtaTextEdit } from "./types";

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * How many edits one pass will consider.
 *
 * PackBench's "fix everything" button can hand this every autofixable finding in
 * a report at once, and a resource with a thousand broken vehicles would
 * otherwise put a thousand-row confirmation dialog on screen and spend the
 * overlap scan's worst case getting there. Anything past the cap is rejected
 * with a reason rather than dropped, so the count the user sees still adds up.
 */
const MAX_EDITS_PER_PASS = 500;

/**
 * How many separate changed regions the dry-run preview will draw.
 *
 * The preview lives in a confirmation dialog, and a dialog nobody scrolls to the
 * bottom of is a dialog nobody read. Beyond the cap the diff says how many more
 * changes it did not draw.
 */
const MAX_DIFF_HUNKS = 60;

/**
 * Lines of unchanged context around each changed region in the preview.
 *
 * Three is the unified-diff convention and, for a `.meta`, it is usually the
 * enclosing `<Item>` — enough for the user to recognise which vehicle is being
 * edited without reading the file.
 */
const DIFF_CONTEXT_LINES = 3;

/**
 * Where a single preview line is cut off.
 *
 * Plenty of shipped `.meta` files are written by an exporter that puts the whole
 * document on one line. Drawing that line in full turns the confirmation dialog
 * into a horizontal scrollbar, so the preview truncates and says so. The
 * truncation is display only; it never reaches the text that gets written.
 */
const MAX_DIFF_LINE_CHARS = 200;

/**
 * The largest file the preview will scan for line boundaries.
 *
 * `applyEdits` has no size limit — a 4 MB `vehicles.meta` still patches — but
 * walking it for newlines to draw a dialog does not earn its cost, so past this
 * the preview says it cannot show one instead of freezing the UI thread.
 */
const MAX_DIFF_SOURCE_CHARS = 2 * 1024 * 1024;

/** How much of a mismatched string a rejection message quotes back. */
const MAX_QUOTED_CHARS = 60;

// ─── Result shape ────────────────────────────────────────────────────────────

/**
 * The outcome of one pass.
 *
 * `applied` and `rejected` together always account for every edit that was
 * handed in, so a caller can render "9 applied, 3 refused" and have the numbers
 * match what happened to the file.
 */
export type EditResult = {
  text: string;
  applied: GtaTextEdit[];
  rejected: { edit: GtaTextEdit; reason: string }[];
};

// ─── Building an edit ────────────────────────────────────────────────────────

/**
 * Build an edit, with the expectation as a required argument.
 *
 * The signature is the entire point: there is no overload that omits `expect`,
 * so a rule that wants to rewrite a span has to say what it believes is there.
 * Nothing is validated here — an impossible span is a rejection at apply time,
 * with a message, rather than a throw at construction time that a report builder
 * would have to guard every call with.
 */
export function textEdit(
  file: string,
  start: number,
  end: number,
  expect: RegExp | string,
  replacement: string,
): GtaTextEdit {
  return { file, start, end, expect, replacement };
}

// ─── Verification ────────────────────────────────────────────────────────────

/** Quote a fragment of file text for a message, cut to a readable length. */
function quote(text: string): string {
  const safe = typeof text === "string" ? text : String(text);
  const shown = safe.length > MAX_QUOTED_CHARS ? `${safe.slice(0, MAX_QUOTED_CHARS)}...` : safe;
  return JSON.stringify(shown);
}

/**
 * How much of the span a pattern matches, anchored at its start.
 *
 * A sticky copy of the pattern is used rather than `test` or `exec` on the
 * original, for two reasons. It forces the match to begin at offset 0, and it
 * leaves the caller's own `RegExp` untouched — a `/g` or `/y` pattern carries
 * `lastIndex` between calls, so verifying with the caller's object would make
 * the second verification of the same edit behave differently from the first.
 *
 * Returns `-1` when the pattern does not match at the start of the span at all.
 */
function matchedLengthAtStart(pattern: RegExp, span: string): number {
  try {
    const flags = `${pattern.flags.replace(/[gy]/g, "")}y`;
    const sticky = new RegExp(pattern.source, flags);
    sticky.lastIndex = 0;
    const match = sticky.exec(span);
    return match ? match[0].length : -1;
  } catch {
    // An unconstructable pattern is a broken fix, not a crash for the caller.
    return -1;
  }
}

/**
 * Why this edit may not be applied to this exact text, or `null` if it may.
 *
 * Reason-or-null rather than the tagged result `verifyEdit` returns, because the
 * project compiles with `strictNullChecks` off and a `{ ok: true } | { ok: false
 * }` union does not narrow under that setting. Every caller inside this module
 * uses this form; `verifyEdit` is the public wrapper over it.
 *
 * Every branch answers with prose rather than a code, because the sentence ends
 * up in front of a user who pressed a fix button and needs to know why the file
 * on disk was left alone.
 */
function refusalFor(source: string, edit: GtaTextEdit): string | null {
  const { start, end } = edit;

  if (typeof edit.replacement !== "string") {
    return (
      "This fix carries no replacement text. Applying it would write the word \"undefined\" into " +
      "the file, which is worse than the problem it was meant to solve, so it was refused."
    );
  }

  if (!Number.isInteger(start) || !Number.isInteger(end)) {
    return (
      `This fix describes the range ${String(start)} to ${String(end)}, which is not a whole ` +
      "range of characters. There is nothing here that can be checked against the file, so it " +
      "was refused."
    );
  }

  if (start > end) {
    return (
      `This fix starts at ${start} and ends at ${end}, which is backwards. Swapping the two ` +
      "would rewrite a stretch of the file nobody asked to change, so it was refused instead."
    );
  }

  if (start < 0 || end > source.length) {
    return (
      `This fix covers characters ${start} to ${end}, but the file is ${source.length} ` +
      "characters long. It was calculated from a different version of this file, so applying " +
      "it would put the replacement somewhere it was never meant to go."
    );
  }

  const span = source.slice(start, end);

  if (typeof edit.expect === "string") {
    if (span === edit.expect) return null;
    return (
      `The file now reads ${quote(span)} at characters ${start} to ${end}, but this fix was ` +
      `calculated for ${quote(edit.expect)}. The file changed since it was analysed, so the ` +
      "fix was refused rather than pasted over whatever is there now. Re-run the check and try " +
      "again."
    );
  }

  if (!(edit.expect instanceof RegExp)) {
    return (
      "This fix does not say what it expects to find at the characters it rewrites, so there is " +
      "no way to tell whether the file is still the one it was calculated from. It was refused."
    );
  }

  const matched = matchedLengthAtStart(edit.expect, span);

  if (matched < 0) {
    return (
      `The file reads ${quote(span)} at characters ${start} to ${end}, which does not match ` +
      `the pattern ${String(edit.expect)} this fix expected. The file changed since it was ` +
      "analysed, so the fix was refused."
    );
  }

  if (matched !== span.length) {
    return (
      `The pattern ${String(edit.expect)} matches only the first ${matched} of the ` +
      `${span.length} characters at ${start} to ${end}, and the rest, ` +
      `${quote(span.slice(matched))}, is unaccounted for. A partial match is not proof that ` +
      "this is the text the fix was calculated from, so the fix was refused."
    );
  }

  return null;
}

/**
 * Whether this edit may be applied to this exact text, right now.
 *
 * The check is the whole reason a `GtaTextEdit` carries an expectation: a fix is
 * a claim about a file as it was when it was analysed, and this is where that
 * claim is tested before anything is written.
 */
export function verifyEdit(
  source: string,
  edit: GtaTextEdit,
): { ok: true } | { ok: false; reason: string } {
  const reason = refusalFor(source, edit);
  return reason === null ? { ok: true } : { ok: false, reason };
}

// ─── Overlap ─────────────────────────────────────────────────────────────────

/**
 * Whether two edits touch, so that applying both would be order-dependent.
 *
 * Spans that merely meet end to end do not overlap: a fix that rewrites a
 * `<modelName>` and a fix that rewrites the `<txdName>` immediately after it are
 * independent, and refusing that pair would make the common case of two findings
 * on adjacent lines unfixable.
 *
 * A zero-length edit is an insertion, and it needs its own rule. An insertion is
 * only in conflict with a surrounding edit when it falls strictly inside it —
 * inserting at the boundary is well defined. Two insertions at the same point
 * do conflict, because the result depends on which one is written first and
 * neither author asked for the other's text to come second.
 */
export function editsOverlap(a: GtaTextEdit, b: GtaTextEdit): boolean {
  const aEmpty = a.start === a.end;
  const bEmpty = b.start === b.end;
  if (aEmpty && bEmpty) return a.start === b.start;
  if (aEmpty) return a.start > b.start && a.start < b.end;
  if (bEmpty) return b.start > a.start && b.start < a.end;
  return a.start < b.end && b.start < a.end;
}

/** True when two edits describe the same characters, whatever they write there. */
function sameSpan(a: GtaTextEdit, b: GtaTextEdit): boolean {
  return a.start === b.start && a.end === b.end;
}

/**
 * The identity of an expectation, so two edits can be compared without running
 * either of them. A pattern's flags matter: `/POLICE/i` and `/POLICE/` accept
 * different files.
 */
function expectKey(expect: RegExp | string): string {
  if (typeof expect === "string") return `s ${expect}`;
  if (expect instanceof RegExp) return `r ${expect.source} ${expect.flags}`;
  // An edit that states no expectation is refused at verification, but it still
  // needs a key here, because nothing in this pass is allowed to throw.
  return "none";
}

/**
 * Two edits that would do exactly the same thing for exactly the same reason.
 *
 * Two rules finding the same broken modkit id is normal — a duplicate id is both
 * a collision and a stock-kit clash — and rejecting the pair as "overlapping"
 * would make a fix that everybody agrees on the one fix that cannot be applied.
 * Identical edits collapse to one instead, and the collapse is still reported.
 */
function duplicateKey(edit: GtaTextEdit): string {
  return `${edit.start} ${edit.end} ${edit.replacement} ${expectKey(edit.expect)}`;
}

// ─── Applying ────────────────────────────────────────────────────────────────

/** Document order, ties broken by the order the caller supplied. */
function inDocumentOrder(edits: readonly GtaTextEdit[]): GtaTextEdit[] {
  return [...edits].sort((a, b) => a.start - b.start || a.end - b.end);
}

/**
 * Verify every edit, drop the ones that cannot be trusted, and write the rest.
 *
 * The result is independent of the order the edits were handed in: they are put
 * into document order first, and both output lists come back in that order too,
 * so a dialog that lists them reads top to bottom like the file does.
 *
 * The rejection rules, in the order they are decided:
 *
 *   1. Edits for more than one file in a single pass reject the whole set. This
 *      function is given one file's text and no way to tell which file that is,
 *      so a mixed list is a caller bug, and guessing which entries belong to the
 *      text in hand is exactly the guess that writes a carcols fix into a
 *      vehicles.meta.
 *   2. Anything past `MAX_EDITS_PER_PASS`.
 *   3. Exact duplicates collapse to the first of their kind.
 *   4. Edits that overlap another edit in the same pass — both of them, always.
 *   5. Edits whose expectation no longer matches the file.
 *
 * Overlap is judged on the request as it was handed in, not on what survived
 * verification. Two fixes that fight over the same characters are a problem with
 * the fixes, and the answer should not change depending on whether the file
 * happens to have drifted underneath them.
 */
export function applyEdits(source: string, edits: readonly GtaTextEdit[]): EditResult {
  const rejected: { edit: GtaTextEdit; reason: string }[] = [];
  const ordered = inDocumentOrder(edits);
  if (ordered.length === 0) return { text: source, applied: [], rejected };

  // 1. One pass patches one file.
  const files = new Set(
    ordered.map((edit) => pathKey(typeof edit.file === "string" ? edit.file : "")),
  );
  if (files.size > 1) {
    const named = [...files].sort().join(", ");
    for (const edit of ordered) {
      rejected.push({
        edit,
        reason:
          `This batch mixes edits for ${files.size} different files (${named}), and a patch can ` +
          "only be applied to one file at a time. Every edit in the batch was refused, because " +
          "guessing which ones belong to this file is how a fix for one resource ends up written " +
          "into another.",
      });
    }
    return { text: source, applied: [], rejected };
  }

  // 2. The cap, applied to the tail so the earliest edits in the file survive.
  const considered = ordered.slice(0, MAX_EDITS_PER_PASS);
  for (const edit of ordered.slice(MAX_EDITS_PER_PASS)) {
    rejected.push({
      edit,
      reason:
        `This batch asks for ${ordered.length} edits at once and only the first ` +
        `${MAX_EDITS_PER_PASS} are applied in one pass. Apply these, re-run the check, and the ` +
        "rest will be offered again against the file as it then stands.",
    });
  }

  // 3. Collapse exact duplicates onto their first occurrence.
  const representativeOf = new Map<string, GtaTextEdit>();
  const representatives: GtaTextEdit[] = [];
  const duplicates: GtaTextEdit[] = [];
  for (const edit of considered) {
    const key = duplicateKey(edit);
    const existing = representativeOf.get(key);
    if (existing) {
      duplicates.push(edit);
      continue;
    }
    representativeOf.set(key, edit);
    representatives.push(edit);
  }

  // 4. Overlap, decided pairwise over the representatives. The scan walks
  //    forward only as far as spans can still reach, which keeps the common
  //    case — many small edits scattered through a file — linear.
  const overlapReason = new Map<GtaTextEdit, string>();
  for (let i = 0; i < representatives.length; i += 1) {
    const a = representatives[i];
    for (let j = i + 1; j < representatives.length; j += 1) {
      const b = representatives[j];
      if (b.start > a.end) break;
      if (!editsOverlap(a, b)) continue;
      const reason = sameSpan(a, b)
        ? `Two fixes rewrite characters ${a.start} to ${a.end} differently — one writes ` +
          `${quote(a.replacement)} and the other writes ${quote(b.replacement)}. Both were ` +
          "refused, because applying either one silently discards the other's finding."
        : `This fix covers characters ${a.start} to ${a.end} and another covers ${b.start} to ` +
          `${b.end}, so they overlap. Both were refused: applying one moves the text the other ` +
          "was calculated from, and half of each fix leaves the file in a state neither intended.";
      if (!overlapReason.has(a)) overlapReason.set(a, reason);
      if (!overlapReason.has(b)) overlapReason.set(b, reason);
    }
  }

  // 5. Verification, which is the only check that looks at the file at all.
  const applied: GtaTextEdit[] = [];
  const reasonOf = new Map<GtaTextEdit, string>();
  for (const edit of representatives) {
    const refusal = refusalFor(source, edit);
    if (refusal !== null) {
      reasonOf.set(edit, refusal);
      continue;
    }
    const overlap = overlapReason.get(edit);
    if (overlap) {
      reasonOf.set(edit, overlap);
      continue;
    }
    applied.push(edit);
  }

  // A duplicate inherits its representative's fate, so the pair is never
  // reported as one success and one mysterious refusal.
  for (const edit of duplicates) {
    const representative = representativeOf.get(duplicateKey(edit));
    const inherited = representative ? reasonOf.get(representative) : undefined;
    reasonOf.set(
      edit,
      inherited ??
        `This fix is character-for-character identical to another in the same batch, covering ` +
          `${edit.start} to ${edit.end}. It was applied once rather than twice; nothing was lost.`,
    );
  }

  for (const edit of considered) {
    const reason = reasonOf.get(edit);
    if (reason !== undefined) rejected.push({ edit, reason });
  }
  rejected.sort((a, b) => a.edit.start - b.edit.start || a.edit.end - b.edit.end);

  // Back to front, so that every remaining offset still refers to the text it
  // was calculated against. Overlap has already been excluded, which is what
  // makes the survivors independent of one another.
  let text = source;
  for (let i = applied.length - 1; i >= 0; i -= 1) {
    const edit = applied[i];
    text = text.slice(0, edit.start) + edit.replacement + text.slice(edit.end);
  }

  return { text, applied, rejected };
}

// ─── Dry run ─────────────────────────────────────────────────────────────────

/** Offsets at which each line of the text begins, including line 0 at offset 0. */
function lineStarts(text: string): number[] {
  const starts = [0];
  for (let i = 0; i < text.length; i += 1) {
    if (text.charCodeAt(i) === 10) starts.push(i + 1);
  }
  return starts;
}

/** The index of the line containing an offset, by binary search. */
function lineIndexAt(starts: number[], offset: number): number {
  let low = 0;
  let high = starts.length - 1;
  while (low < high) {
    const middle = (low + high + 1) >> 1;
    if (starts[middle] <= offset) low = middle;
    else high = middle - 1;
  }
  return low;
}

/**
 * One line as the preview draws it.
 *
 * The trailing carriage return goes because most `.meta` files in circulation
 * were saved on Windows, and a CR left on the end of every line renders as a
 * stray box in the confirmation dialog. This affects the preview only — the text
 * `applyEdits` returns keeps whatever line endings the file had.
 */
function displayLine(line: string): string {
  const bare = line.endsWith("\r") ? line.slice(0, -1) : line;
  return bare.length > MAX_DIFF_LINE_CHARS
    ? `${bare.slice(0, MAX_DIFF_LINE_CHARS)}... (${bare.length} characters)`
    : bare;
}

type DiffHunk = { firstLine: number; lastLine: number; edits: GtaTextEdit[] };

/**
 * What the file would look like afterwards, as something a person can read.
 *
 * This is what goes in front of the user before anything is written, so it is
 * built from the result of `applyEdits` rather than from the request: what the
 * preview shows is exactly what the button does, including the edits that were
 * refused. Refusals are listed first, as `#` comment lines, because a preview
 * that shows two changes when the user asked for three and says nothing about
 * the third is how a fix-all button loses the user's trust.
 *
 * The output is unified-diff *style* — headers, `@@` hunks, `-`/`+`/space
 * prefixes — and readable by anyone who has seen a diff. It is not a patch file:
 * long lines are truncated for display and the hunk count is capped, so feeding
 * it to `patch` is not a supported use. Nothing here modifies anything.
 */
export function dryRunDiff(source: string, edits: readonly GtaTextEdit[]): string {
  const result = applyEdits(source, edits);
  const out: string[] = [];

  for (const { edit, reason } of result.rejected) {
    out.push(`# Refused, characters ${edit.start} to ${edit.end}: ${reason}`);
  }

  if (result.applied.length === 0) {
    out.push("# No changes: nothing in this batch could be applied to the file as it stands.");
    return out.join("\n");
  }

  if (source.length > MAX_DIFF_SOURCE_CHARS) {
    out.push(
      `# ${result.applied.length} change(s) will be made, but this file is ` +
        `${source.length} characters and is too large to preview line by line.`,
    );
    return out.join("\n");
  }

  const file = result.applied[0].file || "file";
  const starts = lineStarts(source);
  const sourceLines = source.split("\n");
  const lastLineIndex = sourceLines.length - 1;

  // Group the edits into changed regions, merging any two that are close enough
  // that their context blocks would run into each other.
  const hunks: DiffHunk[] = [];
  for (const edit of result.applied) {
    const firstLine = lineIndexAt(starts, edit.start);
    const lastLine = edit.end > edit.start ? lineIndexAt(starts, edit.end - 1) : firstLine;
    const previous = hunks[hunks.length - 1];
    if (previous && firstLine - previous.lastLine <= DIFF_CONTEXT_LINES * 2) {
      previous.lastLine = Math.max(previous.lastLine, lastLine);
      previous.edits.push(edit);
      continue;
    }
    hunks.push({ firstLine, lastLine, edits: [edit] });
  }

  out.push(`--- a/${file}`);
  out.push(`+++ b/${file}`);

  const shown = Math.min(hunks.length, MAX_DIFF_HUNKS);
  let lineDelta = 0;
  for (let h = 0; h < shown; h += 1) {
    const hunk = hunks[h];
    const contextStart = Math.max(0, hunk.firstLine - DIFF_CONTEXT_LINES);
    const contextEnd = Math.min(lastLineIndex, hunk.lastLine + DIFF_CONTEXT_LINES);

    // Re-apply this hunk's edits to just the changed lines. Back to front again,
    // for the same reason as in `applyEdits`.
    const regionStart = starts[hunk.firstLine];
    const regionEnd =
      hunk.lastLine + 1 < starts.length ? starts[hunk.lastLine + 1] - 1 : source.length;
    let region = source.slice(regionStart, regionEnd);
    for (let i = hunk.edits.length - 1; i >= 0; i -= 1) {
      const edit = hunk.edits[i];
      region =
        region.slice(0, edit.start - regionStart) +
        edit.replacement +
        region.slice(edit.end - regionStart);
    }

    const before = sourceLines.slice(contextStart, hunk.firstLine);
    const removed = sourceLines.slice(hunk.firstLine, hunk.lastLine + 1);
    const addedLines = region.split("\n");
    const after = sourceLines.slice(hunk.lastLine + 1, contextEnd + 1);

    const oldCount = before.length + removed.length + after.length;
    const newCount = before.length + addedLines.length + after.length;
    const oldStart = contextStart + 1;
    const newStart = oldStart + lineDelta;
    lineDelta += newCount - oldCount;

    out.push(`@@ -${oldStart},${oldCount} +${newStart},${newCount} @@`);
    for (const line of before) out.push(` ${displayLine(line)}`);
    for (const line of removed) out.push(`-${displayLine(line)}`);
    for (const line of addedLines) out.push(`+${displayLine(line)}`);
    for (const line of after) out.push(` ${displayLine(line)}`);
  }

  if (hunks.length > shown) {
    out.push(`# ${hunks.length - shown} further changed region(s) are not shown in this preview.`);
  }

  return out.join("\n");
}
